 
'use client';

import type { Service } from '@/types/service';
import Link from 'next/link';
import {
  ArrowRight,
  CheckCircle,
  Layout,
  ShieldCheck,
  Target,
  Zap,
  Plus,
  Minus,
} from 'lucide-react';
import { useState } from 'react';

interface ServiceDetailProps {
  service: Service;
}

export default function ServiceDetail({ service }: ServiceDetailProps) {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <div className="relative min-h-screen font-body">
      {/* Subtle Background Pattern */}
      {/* <div className="fixed inset-0 pointer-events-none opacity-[0.03]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)`,
            backgroundSize: '40px 40px',
          }}
        />
      </div> */}

      {/* Hero Section - Editorial Layout */}
      <section className="relative z-10 pt-12 md:pt-14 pb-10 md:pb-14">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12 max-w-7xl">
          {/* Tagline */}
          <div className="mb-6 md:mb-8">
            <span className="inline-block px-5 py-2.5 bg-primary text-primary-foreground text-xs font-bold uppercase tracking-[0.3em] rounded-full">
              {service.tagline}
            </span>
          </div>

          {/* Title & Description Grid */}
          <div className="grid lg:grid-cols-12 gap-6 md:gap-8 lg:gap-12 items-start mb-8 md:mb-10">
            <div className="lg:col-span-7">
              <h1 className="text-4xl sm:text-5xl md:text-5xl lg:text-4xl xl:text-5xl font-black font-heading text-foreground leading-[0.95] tracking-tight mb-4 md:mb-6">
                {service.title}
              </h1>
            </div>
            <div className="lg:col-span-5 lg:pt-4">
              <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed">
                {service.description}
              </p>
            </div>
          </div>

          {/* Hero Image */}
          {service.imageUrl && (
            <div className="relative w-full max-w-5xl mx-auto rounded-3xl overflow-hidden group">
              <img
                src={service.imageUrl}
                alt={service.title}
                className="w-full h-auto max-h-[85vh] object-contain transition-transform duration-700"
                style={{ borderRadius: '15px' }}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>
          )}
        </div>
      </section>

      {/* Content Section - Magazine Layout */}
      <section className="relative z-10 py-12 md:py-16 lg:py-24 border-y border-border">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-[1600px]">
          <div className="grid lg:grid-cols-12 gap-6 md:gap-8 lg:gap-12 xl:gap-16">
            {/* Main Content */}
            <div className="lg:col-span-8 space-y-10 md:space-y-14 lg:space-y-20">
              {/* What Is It */}
              <article className="space-y-6">
                <div className="flex items-baseline gap-4 pb-6 border-b-2 border-primary">
                  <span className="text-2xl md:text-3xl font-black font-heading text-primary/20">
                    01
                  </span>
                  <h2 className="text-2xl sm:text-3xl md:text-4xl font-black font-heading text-foreground tracking-tight">
                    {service.whatIsIt.title}
                  </h2>
                </div>
                <div className="bg-muted/50 rounded-3xl p-8 md:p-10 border border-border">
                  <p className="text-lg md:text-xl text-card-foreground leading-relaxed whitespace-pre-line">
                    {service.whatIsIt.content}
                  </p>
                </div>
              </article>

              {/* Deliverables */}
              <article className="space-y-8">
                <div className="flex items-baseline gap-4 pb-6 border-b-2 border-secondary">
                  <span className="text-2xl md:text-3xl font-black font-heading text-secondary/20">
                    02
                  </span>
                  <h2 className="text-2xl sm:text-3xl md:text-4xl font-black font-heading text-foreground tracking-tight">
                    {service.deliverables.title}
                  </h2>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  {service.deliverables.items.map((item, index) => (
                    <div
                      key={index}
                      className="p-6 bg-card rounded-2xl border-2 border-border hover:border-primary hover:shadow-lg hover:shadow-primary/10 transition-all duration-300 group"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                          <CheckCircle className="w-5 h-5 text-primary" />
                        </div>
                        <span className="text-base font-bold text-foreground">{item.text}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </article>

              {/* Use Cases */}
              <article className="space-y-8">
                <div className="flex items-baseline gap-4 pb-6 border-b-2 border-accent">
                  <span className="text-2xl md:text-3xl font-black font-heading text-accent/20">
                    03
                  </span>
                  <h2 className="text-2xl sm:text-3xl md:text-4xl font-black font-heading text-foreground tracking-tight">
                    {service.useCases.title}
                  </h2>
                </div>
                <div className="grid md:grid-cols-2 gap-8">
                  {service.useCases.industries.map((industry, index) => (
                    <div
                      key={index}
                      className="bg-card rounded-3xl p-8 border-2 border-border hover:border-secondary/50 hover:shadow-xl hover:shadow-secondary/5 transition-all duration-500"
                    >
                      <div className="w-14 h-14 rounded-2xl bg-secondary/10 flex items-center justify-center mb-6">
                        <Layout className="w-7 h-7 text-secondary" />
                      </div>
                      <h3 className="text-2xl font-black font-heading text-foreground mb-6">
                        {industry.title}
                      </h3>
                      <ul className="space-y-3 pl-6 border-l-4 border-accent/30">
                        {industry.items.map((item, itemIndex) => (
                          <li key={itemIndex} className="text-muted-foreground leading-relaxed">
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </article>
            </div>

            {/* Sidebar */}
            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-32 space-y-8">
                {/* Approach Card */}
                <div className="bg-gradient-to-br from-primary to-secondary text-primary-foreground p-8 md:p-10 rounded-3xl shadow-2xl shadow-primary/20 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />

                  <h3 className="text-2xl md:text-3xl font-black font-heading mb-6 tracking-tight relative z-10">
                    {service.approach.title}
                  </h3>
                  <p className="text-primary-foreground/90 leading-relaxed mb-8 relative z-10">
                    {service.approach.content}
                  </p>

                  <div className="space-y-4 mb-8 relative z-10">
                    <h4 className="text-xs font-black uppercase tracking-[0.3em] text-primary-foreground/60">
                      {service.approach.processTitle}
                    </h4>
                    <div className="space-y-3">
                      {service.approach.processSteps.map((step, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-3 p-3 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 transition-all"
                        >
                          <span className="text-sm font-black w-8 h-8 rounded-lg flex items-center justify-center bg-white text-primary flex-shrink-0">
                            {index + 1}
                          </span>
                          <span className="text-sm font-bold text-primary-foreground">
                            {step.text}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Link
                    href="/book-meeting"
                    className="block w-full text-center px-8 py-4 bg-white text-primary rounded-2xl font-black text-lg hover:scale-[1.02] hover:shadow-xl transition-all relative z-10"
                  >
                    Start Project
                  </Link>
                </div>

                {/* Custom Solution */}
                <div className="p-8 rounded-3xl bg-accent/10 border-2 border-accent/20 hover:border-accent/40 transition-all">
                  <h4 className="text-sm font-black uppercase tracking-[0.2em] text-accent-foreground/70 mb-4">
                    Need Custom?
                  </h4>
                  <p className="text-foreground/80 mb-6">
                    We can tailor our systems to your specific business architecture.
                  </p>
                  <Link
                    href="/book-meeting"
                    className="text-primary font-black flex items-center gap-2 hover:gap-3 transition-all group"
                  >
                    Book Strategy Session
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-12 md:py-16 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black font-heading text-foreground tracking-tight mb-8 md:mb-10 text-center">
            Frequently Asked <span className="text-primary">Questions</span>
          </h2>

          <div className="space-y-4">
            {service.faqs.map((faq, index) => (
              <div
                key={index}
                className="rounded-3xl border-2 border-border bg-card overflow-hidden hover:border-primary/30 transition-all"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 md:p-8 text-left hover:bg-muted/30 transition-colors"
                >
                  <h3 className="text-lg md:text-xl font-black font-heading text-foreground pr-8">
                    {faq.question}
                  </h3>
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center bg-primary/10 text-primary flex-shrink-0 group-hover:scale-110 transition-transform">
                    {openFaq === index ? (
                      <Minus className="w-5 h-5" />
                    ) : (
                      <Plus className="w-5 h-5" />
                    )}
                  </div>
                </button>
                {openFaq === index && (
                  <div className="px-6 md:px-8 pb-6 md:pb-8 bg-muted/20 animate-slide-down">
                    <div className="pt-4 border-t border-border">
                      <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-12 md:py-16 lg:py-24 bg-foreground text-background relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div
            className="absolute inset-0 bg-gradient-to-br from-primary via-secondary to-accent animate-gradient-pan"
            style={{ backgroundSize: '200% 200%' }}
          />
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl relative z-10">
          <div className="text-center space-y-6 md:space-y-8">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black font-heading tracking-tight leading-none text-background">
              {service.cta.title}
            </h2>
            <p className="text-xl md:text-2xl text-background/70 leading-relaxed max-w-3xl mx-auto">
              {service.cta.content}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-8">
              <Link
                href="/book-meeting"
                className="w-full sm:w-auto px-10 py-5 bg-primary text-primary-foreground rounded-2xl font-black text-xl hover:scale-[1.02] hover:shadow-2xl hover:shadow-primary/30 transition-all"
              >
                Book Free consultation call
              </Link>
             
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
